import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { X, Plus, Trash2, GripVertical, Check, Move } from 'lucide-react';
import { Hotspot } from '@/hooks/useHotspots';
import { toast } from 'sonner';

interface HotspotEditorProps {
  hotspots: Hotspot[];
  onAdd: (hotspot: Omit<Hotspot, 'id' | 'is_active' | 'position'>) => Promise<Hotspot>;
  onUpdate: (id: string, updates: Partial<Hotspot>) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onClose: () => void;
  imageRef: React.RefObject<HTMLImageElement>;
}

export const HotspotEditor = ({
  hotspots,
  onAdd,
  onUpdate,
  onDelete,
  onClose,
  imageRef,
}: HotspotEditorProps) => {
  const [editingHotspot, setEditingHotspot] = useState<Hotspot | null>(null);
  const [isAddMode, setIsAddMode] = useState(false);
  const [newHotspot, setNewHotspot] = useState<Partial<Hotspot>>({
    label: '',
    description: '',
    tooltip_position: 'top',
  });
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  
  // Panel dragging state
  const [panelPosition, setPanelPosition] = useState({ x: 16, y: 16 });
  const [isDraggingPanel, setIsDraggingPanel] = useState(false);
  const [panelDragOffset, setPanelDragOffset] = useState({ x: 0, y: 0 });
  const panelRef = useRef<HTMLDivElement>(null);

  // Handle panel dragging
  useEffect(() => {
    if (!isDraggingPanel) return;

    const handleMouseMove = (e: MouseEvent) => {
      const newX = e.clientX - panelDragOffset.x;
      const newY = e.clientY - panelDragOffset.y;
      
      // Keep panel within viewport
      const maxX = window.innerWidth - (panelRef.current?.offsetWidth || 300);
      const maxY = window.innerHeight - (panelRef.current?.offsetHeight || 200);
      
      setPanelPosition({
        x: Math.max(0, Math.min(maxX, newX)),
        y: Math.max(0, Math.min(maxY, newY)),
      });
    };

    const handleMouseUp = () => {
      setIsDraggingPanel(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDraggingPanel, panelDragOffset]);

  const handlePanelDragStart = (e: React.MouseEvent) => {
    if (!panelRef.current) return;
    const rect = panelRef.current.getBoundingClientRect();
    setPanelDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
    setIsDraggingPanel(true);
  };

  const handleImageClick = async (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isAddMode || !imageRef.current) return;

    const rect = imageRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    if (!newHotspot.label || !newHotspot.description) {
      toast.error('Please fill in label and description first');
      return;
    }

    try {
      await onAdd({
        label: newHotspot.label,
        description: newHotspot.description,
        top_percent: y,
        left_percent: x,
        tooltip_position: newHotspot.tooltip_position || 'top',
      });
      toast.success('Hotspot added');
      setIsAddMode(false);
      setNewHotspot({ label: '', description: '', tooltip_position: 'top' });
    } catch (error) {
      toast.error('Failed to add hotspot');
    }
  };

  const handleDragStart = (e: React.MouseEvent, hotspot: Hotspot) => {
    if (!imageRef.current) return;
    
    const rect = imageRef.current.getBoundingClientRect();
    const hotspotX = (hotspot.left_percent / 100) * rect.width + rect.left;
    const hotspotY = (hotspot.top_percent / 100) * rect.height + rect.top;
    
    setDragOffset({
      x: e.clientX - hotspotX,
      y: e.clientY - hotspotY,
    });
    setDraggingId(hotspot.id);
  };

  const handleDrag = async (e: React.MouseEvent) => {
    if (!draggingId || !imageRef.current) return;

    const rect = imageRef.current.getBoundingClientRect();
    const x = ((e.clientX - dragOffset.x - rect.left) / rect.width) * 100;
    const y = ((e.clientY - dragOffset.y - rect.top) / rect.height) * 100;

    // Clamp values between 0 and 100
    const clampedX = Math.max(0, Math.min(100, x));
    const clampedY = Math.max(0, Math.min(100, y));

    try {
      await onUpdate(draggingId, {
        left_percent: clampedX,
        top_percent: clampedY,
      });
    } catch (error) {
      console.error('Failed to update position');
    }
  };

  const handleDragEnd = () => {
    setDraggingId(null);
  };

  const handleSaveEdit = async () => {
    if (!editingHotspot) return;

    try {
      await onUpdate(editingHotspot.id, {
        label: editingHotspot.label,
        description: editingHotspot.description,
        tooltip_position: editingHotspot.tooltip_position,
      });
      toast.success('Hotspot updated');
      setEditingHotspot(null);
    } catch (error) {
      toast.error('Failed to update hotspot');
    }
  };

  const handleDeleteHotspot = async (id: string) => {
    try {
      await onDelete(id);
      toast.success('Hotspot deleted');
      setEditingHotspot(null);
    } catch (error) {
      toast.error('Failed to delete hotspot');
    }
  };

  return (
    <>
      {/* Editor Overlay */}
      <div
        className="absolute inset-0 z-50"
        onClick={handleImageClick}
        onMouseMove={draggingId ? handleDrag : undefined}
        onMouseUp={handleDragEnd}
        onMouseLeave={handleDragEnd}
        style={{ cursor: isAddMode ? 'crosshair' : 'default' }}
      >
        {/* Existing hotspots as draggable points */}
        {hotspots.map((hotspot) => (
          <div
            key={hotspot.id}
            className={`absolute transform -translate-x-1/2 -translate-y-1/2 cursor-move ${
              draggingId === hotspot.id ? 'z-[60]' : ''
            }`}
            style={{
              top: `${hotspot.top_percent}%`,
              left: `${hotspot.left_percent}%`,
            }}
            onMouseDown={(e) => {
              e.stopPropagation();
              handleDragStart(e, hotspot);
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (!draggingId) {
                setEditingHotspot(hotspot);
              }
            }}
          >
            <div className="relative">
              <div className="w-6 h-6 rounded-full bg-primary border-2 border-white shadow-lg flex items-center justify-center">
                <GripVertical className="h-3 w-3 text-primary-foreground" />
              </div>
              <div className="absolute left-8 top-1/2 -translate-y-1/2 bg-background border border-border rounded px-2 py-1 text-xs whitespace-nowrap shadow-md">
                {hotspot.label}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Control Panel */}
      <div 
        ref={panelRef}
        className="fixed z-[60] bg-background border border-border rounded-lg shadow-xl p-4 space-y-4 max-w-xs"
        style={{ left: panelPosition.x, top: panelPosition.y }}
      >
        <div className="flex items-center justify-between gap-2">
          <div 
            className="cursor-move p-1 hover:bg-muted rounded"
            onMouseDown={handlePanelDragStart}
          >
            <Move className="h-4 w-4 text-muted-foreground" />
          </div>
          <h3 className="font-semibold text-sm flex-1">Hotspot Editor</h3>
          <Button size="icon" variant="ghost" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        {isAddMode ? (
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">Label</Label>
              <Input
                value={newHotspot.label || ''}
                onChange={(e) => setNewHotspot({ ...newHotspot, label: e.target.value })}
                placeholder="Feature name"
                className="h-8 text-sm"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Description</Label>
              <Input
                value={newHotspot.description || ''}
                onChange={(e) => setNewHotspot({ ...newHotspot, description: e.target.value })}
                placeholder="Feature description"
                className="h-8 text-sm"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Tooltip Position</Label>
              <Select
                value={newHotspot.tooltip_position}
                onValueChange={(v) => setNewHotspot({ ...newHotspot, tooltip_position: v })}
              >
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="top">Top</SelectItem>
                  <SelectItem value="bottom">Bottom</SelectItem>
                  <SelectItem value="left">Left</SelectItem>
                  <SelectItem value="right">Right</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <p className="text-xs text-muted-foreground">Click on the image to place the hotspot</p>
            <Button size="sm" variant="outline" className="w-full" onClick={() => setIsAddMode(false)}>
              Cancel
            </Button>
          </div>
        ) : (
          <>
            <Button size="sm" className="w-full" onClick={() => setIsAddMode(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Hotspot
            </Button>
            <p className="text-xs text-muted-foreground">
              Drag hotspots to reposition. Click to edit.
            </p>
            <div className="text-xs text-muted-foreground border-t pt-2">
              {hotspots.length} hotspot{hotspots.length !== 1 ? 's' : ''}
            </div>
          </>
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={!!editingHotspot} onOpenChange={() => setEditingHotspot(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Hotspot</DialogTitle>
          </DialogHeader>
          {editingHotspot && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Label</Label>
                <Input
                  value={editingHotspot.label}
                  onChange={(e) => setEditingHotspot({ ...editingHotspot, label: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Input
                  value={editingHotspot.description}
                  onChange={(e) => setEditingHotspot({ ...editingHotspot, description: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Tooltip Position</Label>
                <Select
                  value={editingHotspot.tooltip_position}
                  onValueChange={(v) => setEditingHotspot({ ...editingHotspot, tooltip_position: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="top">Top</SelectItem>
                    <SelectItem value="bottom">Bottom</SelectItem>
                    <SelectItem value="left">Left</SelectItem>
                    <SelectItem value="right">Right</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2">
                <Button onClick={handleSaveEdit} className="flex-1">
                  <Check className="h-4 w-4 mr-2" />
                  Save
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => handleDeleteHotspot(editingHotspot.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
